// React hooks and styling
import { useEffect, useState, useCallback } from "react";

// API endpoint
import { ENDPOINT } from "../../../data/FormData";

// React Router hook to get URL params
import { useParams } from "react-router";

// Data helper 
import { RegistrationData } from "../../../data/RegistrationData";

// Registration Form component
import RegistrationFormSingleProgram from "./components/RegistrationFormSingleProgram";

// Static contact component for support
import { support_contact } from "../../../res/support"

// Error popup
import { ErrorPopup } from "../../../components/ErrorPopup";

// Utility functions
import { validateInput, validateMultiSelectInput, validateEmailInput, validatePhoneNumberInput } from "../../../utility-function/InputValidation";

// API Response
import { ValidProviderProgramAPIResponse } from "./types"


function PatientRegistrationSingleProgram(): JSX.Element {

    // Form and state management
    const [validProviderAndProgram, setValidProviderAndProgram] = useState<boolean>(true);
    const { provider, program } = useParams() // Get provider and program from URL

    const [apiResponse, setApiResponse] = useState<ValidProviderProgramAPIResponse | null>(null)

    const [selectedLanguage, setSelectedLanguage] = useState<string>('')
    const [validSelectedLanguage, setValidSelectedLanguage] = useState<boolean>(true)

    const [firstNameValue, setFirstNameValue] = useState<string>('')
    const [validFirstName, setValidFirstName] = useState<boolean>(true)

    const [lastNameValue, setLastNameValue] = useState<string>('')
    const [validLastName, setValidLastName] = useState<boolean>(true)

    const [phoneValue, setPhoneValue] = useState<string>('')
    const [validPhoneNumber, setValidPhoneNumber] = useState<boolean>(true)

    const [emailValue, setEmailValue] = useState<string>('')
    const [validEmail, setValidEmail] = useState<boolean>(true)

    const [preferredContactMethodValue, setPreferredContactMethod] = useState<string[]>([])
    const [validPreferredContactMethod, setValidPreferredContactMethod] = useState<boolean>(true)

    const [bpCuffValue, setBpCuffValue] = useState<string>('')
    const [validBpCuff, setValidBpCuff] = useState<boolean>(true)

    const [glucometerValue, setGlucometerValue] = useState<string>('')
    const [validGlucometer, setValidGlucometer] = useState<boolean>(true)

    const [studyArmValue, setStudyArmValue] = useState<string>('')
    const [validStudyArm, setValidStudyArm] = useState<boolean>(true)

    const [addressStreetValue, setAddressStreetValue] = useState<string>('')
    const [validAddressStreet, setValidAddressStreet] = useState<boolean>(true)

    const [addressCityValue, setAddressCityValue] = useState<string>('')
    const [validAddressCity, setValidAddressCity] = useState<boolean>(true)

    const [addressStateValue, setAddressStateValue] = useState<string>('')
    const [validAddressState, setValidAddresState] = useState<boolean>(true)

    const [addressZipCodeValue, setAddressZipCodeValue] = useState<string>('')
    const [validAddressZipCode, setValidAddresZipCode] = useState<boolean>(true)

    const [statusMessage, setStatusMessage] = useState<JSX.Element | undefined>(undefined)
    const [showRegistrationForm, setShowRegistrationForm] = useState<boolean>(true)
    const [submissionInProgress, setSubmissionInProgress] = useState<boolean>(false)
    const [errorPopupMessage, setErrorPopupMessage] = useState<string | undefined>(undefined)


    // Initial fetch to validate provider and program from URL
    useEffect(() => {
        fetch(`${ENDPOINT}/validproviderprogram/${provider}/${program}`)
            .then(response => {
                if (!response.ok) {
                    setValidProviderAndProgram(false)
                    setShowRegistrationForm(false)
                }
                return response.json()
            })
            .then(data => { setApiResponse(data) })
            .catch(err => { console.log(err) })
    }, [program, provider])


    // Handler for when the input information already belongs to a registered user
    const handleAlreadyRegisteredResponse = (
        email_registered: boolean,
        phone_registered: boolean
    ): void => {
        setSubmissionInProgress(false);

        if (email_registered && phone_registered) {
            setStatusMessage(
                <div>
                    <p>
                        The email address <b>{emailValue}</b> and phone number <b>+{phoneValue}</b> have already been
                        registered.
                    </p>
                    {support_contact}
                </div>
            );
        } else if (email_registered) {
            setStatusMessage(
                <div>
                    <p>The email address <b>{emailValue}</b> has already been registered.</p>
                    {support_contact}
                </div>
            );
        } else if (phone_registered) {
            setStatusMessage(
                <div>
                    <p>The phone number <b>+{phoneValue}</b> has already been registered.</p>
                    {support_contact}
                </div>
            );
        }
    };


    // Handler for when the input information is for a new user
    const handleNewRegistration = (): void => {
        RegistrationData.instance.registerUser(
            provider!,
            [program!],
            selectedLanguage,
            firstNameValue,
            lastNameValue,
            phoneValue,
            emailValue,
            preferredContactMethodValue,
            bpCuffValue,
            glucometerValue,
            studyArmValue,
            RegistrationData.instance.getFullAddress(
                addressStreetValue,
                addressCityValue,
                addressStateValue,
                addressZipCodeValue
            ),
            "", // subscription not used here
            () => {
                // success callback
                setSubmissionInProgress(false)
                setShowRegistrationForm(false);
                setStatusMessage(
                    <div style={{ color: 'green' }}>
                        Thanks for signing up! We sent you an email to complete your mobile app account setup. A team member will contact you shortly to help finish your onboarding.
                    </div>
                )
            },
            (errorMessage?: string) => {
                // error callback
                setSubmissionInProgress(false)
                if (errorMessage) {
                    setErrorPopupMessage(errorMessage)
                } else {
                    setStatusMessage(
                        <div>
                            Your request cannot be processed at the moment. Please try again later.
                            {support_contact}
                        </div>
                    )
                }
            }
        )
    }


    // Submit form handler
    const submit = useCallback((): void => {
        // Validate inputs
        let isLanguageValid = validateInput(selectedLanguage);
        let isFirstNameValid = validateInput(firstNameValue);
        let isLastNameValid = validateInput(lastNameValue);
        let isPhoneValid = validatePhoneNumberInput(phoneValue);
        let isEmailValid = validateEmailInput(emailValue);
        let isPreferredContantValid = validateMultiSelectInput(preferredContactMethodValue);

        let isBpCuffValid = true;
        if (apiResponse?.show_bp_cuff_question) {
            isBpCuffValid = validateInput(bpCuffValue);
        }

        let isGlucometerValid = true;
        if (apiResponse?.show_glucometer_question) {
            isGlucometerValid = validateInput(glucometerValue);
        }

        let isStudyArmValid = true;
        if (apiResponse?.show_study_arm_question) {
            isStudyArmValid = validateInput(studyArmValue);
        }

        let isAddressStreetValid = true;
        let isAddressCityValid = true;
        let isAddressStateValid = true;
        let isAddressZipCodeValid = true;
        if (bpCuffValue === "No" || glucometerValue === "No") {
            isAddressStreetValid = validateInput(addressStreetValue);
            isAddressCityValid = validateInput(addressCityValue);
            isAddressStateValid = validateInput(addressStateValue);
            isAddressZipCodeValid = validateInput(addressZipCodeValue);
        }

        setValidSelectedLanguage(isLanguageValid);
        setValidFirstName(isFirstNameValid);
        setValidLastName(isLastNameValid);
        setValidPhoneNumber(isPhoneValid);
        setValidEmail(isEmailValid);
        setValidPreferredContactMethod(isPreferredContantValid);
        setValidBpCuff(isBpCuffValid);
        setValidGlucometer(isGlucometerValid);
        setValidStudyArm(isStudyArmValid);
        setValidAddressStreet(isAddressStreetValid);
        setValidAddressCity(isAddressCityValid);
        setValidAddresState(isAddressStateValid);
        setValidAddresZipCode(isAddressZipCodeValid);

        if (!isLanguageValid || !isFirstNameValid || !isLastNameValid || !isPhoneValid ||
            !isEmailValid || !isPreferredContantValid || !isBpCuffValid || !isGlucometerValid ||
            !isStudyArmValid || !isAddressStreetValid || !isAddressCityValid || !isAddressStateValid || !isAddressZipCodeValid) {
            return; // Stop if validation fails
        }

        setSubmissionInProgress(true) // Show spinner and disable button

        // Check if user is already registered and take action based on the response
        RegistrationData.instance.userAlreadyRegistered(
            emailValue,
            phoneValue,
            handleAlreadyRegisteredResponse,
            handleNewRegistration
        )
    }, [
        provider,
        program,
        selectedLanguage,
        firstNameValue,
        lastNameValue,
        phoneValue,
        emailValue,
        preferredContactMethodValue,
        bpCuffValue,
        glucometerValue,
        studyArmValue,
        addressStreetValue,
        addressCityValue,
        addressStateValue,
        addressZipCodeValue
    ]);

    const form = RegistrationFormSingleProgram({
        provider,
        program,
        validProviderAndProgram,
        showRegistrationForm,
        apiResponse,

        selectedLanguage,
        setSelectedLanguage,
        validSelectedLanguage,

        firstNameValue,
        setFirstNameValue,
        validFirstName,

        lastNameValue,
        setLastNameValue,
        validLastName,

        phoneValue,
        setPhoneValue,
        validPhoneNumber,

        emailValue,
        setEmailValue,
        validEmail,

        preferredContactMethodValue,
        setPreferredContactMethod,
        validPreferredContactMethod,

        bpCuffValue,
        setBpCuffValue,
        validBpCuff,

        glucometerValue,
        setGlucometerValue,
        validGlucometer,

        studyArmValue,
        setStudyArmValue,
        validStudyArm,

        addressStreetValue,
        setAddressStreetValue,
        validAddressStreet,
        addressCityValue,
        setAddressCityValue,
        validAddressCity,
        addressStateValue,
        setAddressStateValue,
        validAddressState,
        addressZipCodeValue,
        setAddressZipCodeValue,
        validAddressZipCode,

        statusMessage,
        submit,
        submissionInProgress
    })

    return (
        <>
            {form}
            {errorPopupMessage && (
                <ErrorPopup
                    message={errorPopupMessage}
                    onClose={() => setErrorPopupMessage(undefined)}
                />
            )}
        </>
    )
}

export default PatientRegistrationSingleProgram;